import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Paper, GlobalStyles, Link, Button } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import visitImage    from '../assets/makeAVisit/visitMuseum.JPG';
import teachersImage from '../assets/visitors/teachers.JPG';
import visitorImage  from '../assets/visitors/visitor.JPG';
import picnicImage   from '../assets/makeAVisit/picnic.JPG';
import BookingForm   from '../components/Visit/BookingForm';

export default function Visit() {
    const [selectedSlot, setSelectedSlot] = useState(null);
    const bookingRef = useRef(null);
    useEffect(() => { window.scrollTo(0, 0); }, []);

    const scrollToBooking = () => {
        const el = bookingRef.current;
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    return (
        <>
            {/* Hero */}
            <Box
                sx={{
                    position: 'relative',
                    height: { xs: 260, md: 320 },
                    backgroundImage: `url(${visitImage})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center 55%',
                    backgroundRepeat: 'no-repeat',
                }}
            >
                <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.5)' }} />
                <Box
                    sx={{
                        position: 'relative', zIndex: 1, height: '100%',
                        display: 'flex', flexDirection: 'column',
                        alignItems: 'center', justifyContent: 'center', px: 2, gap: 2,
                    }}
                >
                    <Typography
                        variant="h2"
                        sx={{ color: '#fff', fontWeight: 700, textAlign: 'center', textShadow: '2px 2px 6px rgba(0,0,0,0.7)' }}
                    >
                        Plan Your Visit
                    </Typography>
                    <Button
                        variant="contained"
                        size="large"
                        onClick={scrollToBooking}
                        sx={{
                            display: { xs: 'inline-flex', sm: 'none' },
                            bgcolor: '#002F6C',
                            '&:hover': { bgcolor: '#001F4C' }
                        }}
                    >
                        Book Now
                    </Button>
                </Box>
            </Box>

            <Box
                sx={{
                    p: 4,
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', md: '50% 35%' },
                    gap: 4.5,
                    justifyContent: 'center',
                }}
            >
                {/* Info Sidebar */}
                <Paper elevation={1} sx={{ p: 3, position: { md: 'sticky' }, top: 16 }}>
                    <GlobalStyles styles={{ '.react-datepicker-popper': { zIndex: 2000 } }} />

                    <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 700 }}>
                        Visitor Information
                    </Typography>

                    {/* Hours callout — refreshed color scheme */}
                    <Box
                        sx={{
                            mb: 3,
                            p: 2,
                            borderRadius: 1.5,
                            background: 'linear-gradient(90deg, #0f172a, #1e3a8a)',
                            color: 'white',
                            boxShadow: '0 6px 18px rgba(0,0,0,0.12)',
                            border: '1px solid rgba(248, 191, 88, 0.35)' /* subtle warm accent */
                        }}
                        aria-label="Museum Hours"
                    >
                        <Typography variant="subtitle2" sx={{ fontWeight: 800, letterSpacing: 0.3, mb: 0.5 }}>
                            Museum Hours
                        </Typography>
                        <Typography variant="body2">
                            <strong>Fridays:</strong> 10:00 a.m. – 3:00 p.m.
                        </Typography>
                        <Typography variant="body2">
                            <strong>2nd &amp; 4th Saturdays:</strong> 10:00 a.m. – 1:00 p.m.
                        </Typography>
                        <Typography variant="body2">
                            <strong>Sundays:</strong> 1:30 p.m. – 4:00 p.m.
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 1 }}>
                            <em>Closed on major holidays.</em>
                        </Typography>
                    </Box>

                    <Box
                        sx={{
                            mb: 4, p: 2, borderRadius: 1, bgcolor: 'rgba(15,23,42,0.05)',
                            borderLeft: '4px solid #0f172a'
                        }}
                    >
                        <Typography variant="body2" sx={{ mb: 0.5 }}>
                            <strong>Admission:</strong> <strong>$5 per person.</strong><br />
                            Enjoy <strong>unlimited visits</strong> when you become a member for <strong>$25/year</strong>{' '}
                            <Link component={RouterLink} to="/membership" underline="hover">Become a Member</Link>.
                        </Typography>
                        <Typography variant="body2">
                            Prefer to help out? <strong>Volunteers can have the membership fee waived.</strong>{' '}
                            <Link component={RouterLink} to="/volunteer" underline="hover">Become a Volunteer</Link>.
                        </Typography>
                    </Box>

                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
                        School Field Trips
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'flex-start', gap: 3, mb: 5 }}>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="body1" paragraph>
                                Bring your class to explore Alabama, Calhoun County, and Piedmont history. Enjoy a snack on the newly renovated umbrella deck.
                            </Typography>
                        </Box>
                        <Box component="img" src={teachersImage} alt="Students on a field trip"
                             sx={{ width: { xs: '100%', md: 300 }, height: { xs: 'auto', md: 200 }, objectFit: 'cover', borderRadius: 1, boxShadow: 1 }} />
                    </Box>

                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
                        Individual and Group Visits
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'flex-start', gap: 3, mb: 5 }}>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="body1" paragraph>
                                Come on your own or bring a group. Reunions, parties, and private events can rent the
                                Depot for the day — see Depot Event Rentals below.
                            </Typography>
                        </Box>
                        <Box component="img" src={visitorImage} alt="Visitors exploring the museum"
                             sx={{ width: { xs: '100%', md: 300 }, height: { xs: 'auto', md: 200 }, objectFit: 'cover', borderRadius: 1, boxShadow: 1 }} />
                    </Box>

                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
                        Depot Event Rentals
                    </Typography>
                    <Box
                        sx={{
                            mb: 5, p: 2, borderRadius: 1, bgcolor: 'rgba(15,23,42,0.05)',
                            borderLeft: '4px solid #0f172a'
                        }}
                    >
                        <Typography variant="body1" sx={{ mb: 1 }}>
                            Rent the Depot for <strong>$150 per day</strong> — class reunions, teas, meetings,
                            showers, and other events. A rental reserves the entire Depot for your date.
                        </Typography>
                        <Typography variant="body2">
                            To book, select <strong>“Depot Rental — Event ($150/day)”</strong> as the visit type
                            in the booking form and pick your date. We’ll follow up to coordinate access and
                            setup times.
                        </Typography>
                    </Box>

                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
                        Dinner at the Depot
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, alignItems: 'flex-start', gap: 3, mb: 2 }}>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="body1" paragraph>
                                Coming soon: Come join us for Dinner at the Depot! We will have food trucks outside of the museum, and you can enjoy the dining area of our new renovated deck for free! Enjoy a night of food, friends, and a great view of Dugger Mountain!
                            </Typography>
                        </Box>
                        <Box component="img" src={picnicImage} alt="Dinner at the Depot"
                             sx={{ width: { xs: '100%', md: 300 }, height: { xs: 'auto', md: 200 }, objectFit: 'cover', borderRadius: 1, boxShadow: 1 }} />
                    </Box>
                </Paper>

                {/* Booking Form & Calendar */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                    <Box id="booking-start" ref={bookingRef} sx={{ scrollMarginTop: { xs: 74, md: 120 } }} />
                    <BookingForm slot={selectedSlot} onClear={() => setSelectedSlot(null)} />
                </Box>
            </Box>
        </>
    );
}
